import { useState } from 'react';
import { SaveOutlined } from '@ant-design/icons';

import { Tile, TileBody, TileHeader } from '../../../components/shared';
import { Button, Checkbox, Field, Input, Select, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { loiTheoConTro } from '../../vai-tro/nghiep-vu/vai-tro';
import { CO_XU_LY, MO_CHE_DO, NHAN_CHE_DO, NHAN_CO, NHAN_LOAI } from '../constants';
import { tinhPatch } from '../nghiep-vu/loai-chung-tu';
import { DOCUMENT_TYPES, EXTRACTION_MODES } from '../types';
import type { DocumentType, ExtractionMode, OcrSchemaDetail, OcrSchemaPatch } from '../types';
import { ChonModel } from './ChonModel';

type BanNhap = {
  name: string;
  type: DocumentType;
  description: string;
  extraction_mode: ExtractionMode;
  ocr_provider: string | null;
  ocr_reader_provider: string | null;
  is_active: boolean;
  split_pdf_pages: boolean;
  enable_word_boxes: boolean;
  transform_by_prompt: boolean;
  model_self_map: boolean;
};

const tuSchema = (s: OcrSchemaDetail): BanNhap => ({
  name: s.name,
  type: s.type,
  description: s.description ?? '',
  extraction_mode: s.extraction_mode,
  ocr_provider: s.ocr_provider,
  ocr_reader_provider: s.ocr_reader_provider,
  is_active: s.is_active,
  split_pdf_pages: s.split_pdf_pages,
  enable_word_boxes: s.enable_word_boxes,
  transform_by_prompt: s.transform_by_prompt,
  model_self_map: s.model_self_map,
});

/**
 * Khối thông tin chung của loại chứng từ (`PATCH /schemas/{id}`): sửa tại chỗ, nút lưu chỉ hiện khi
 * có thay đổi và chỉ gửi trường đổi. Không có quyền sửa thì mọi ô chỉ đọc. Phía gọi đặt `key` theo
 * `updated_at` để bản nháp nạp lại sau khi lưu hay khi có bản mới.
 */
export function KhoiThongTinChung({
  schema,
  coSua,
  dangLuu,
  onLuu,
}: {
  schema: OcrSchemaDetail;
  coSua: boolean;
  dangLuu: boolean;
  onLuu: (du: OcrSchemaPatch) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const goc = tuSchema(schema);
  const [ban, setBan] = useState<BanNhap>(goc);
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{ loi: ApiError; truong: Record<string, string> } | null>(null);
  const doi = <K extends keyof BanNhap>(k: K, v: BanNhap[K]) => {
    setBan((b) => ({ ...b, [k]: v }));
    setLoiServer(null);
  };
  const moi = { ...ban, ocr_reader_provider: ban.extraction_mode === 'OCR_THEN_MAP' ? ban.ocr_reader_provider : null };
  const patch = tinhPatch<BanNhap>(goc, moi, ['description']);
  const coDoi = Object.keys(patch).length > 0;
  const loiTen = (daGui && !ban.name.trim() ? t('cauHinhOcr.fTenTrong') : undefined) ?? loiServer?.truong['/name'];
  const loiDoc =
    (daGui && ban.extraction_mode === 'OCR_THEN_MAP' && !ban.ocr_reader_provider ? t('cauHinhOcr.fModelDocTrong') : undefined) ??
    loiServer?.truong['/ocr_reader_provider'];

  const luu = async () => {
    setDaGui(true);
    if (!ban.name.trim() || (ban.extraction_mode === 'OCR_THEN_MAP' && !ban.ocr_reader_provider) || !coDoi) return;
    setLoiServer(null);
    try {
      await onLuu(patch as OcrSchemaPatch);
    } catch (err) {
      setLoiServer({ loi: err as ApiError, truong: loiTheoConTro((err as ApiError).details) });
    }
  };

  return (
    <Tile span="col-span-12">
      <TileHeader
        title={t('cauHinhOcr.khoiChung')}
        mo={t('cauHinhOcr.khoiChungMo')}
        action={
          coSua && coDoi ? (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setBan(goc)}>
                {t('cauHinhOcr.boThayDoi')}
              </Button>
              <Button variant="primary" size="sm" icon={<SaveOutlined />} loading={dangLuu} onClick={() => void luu()}>
                {dangLuu ? t('chung.dangLuu') : t('cauHinhOcr.luuThayDoi')}
              </Button>
            </div>
          ) : undefined
        }
      />
      <TileBody className="space-y-4">
        {loiServer && <LoiTrongHop>{dichLoi(loiServer.loi)}</LoiTrongHop>}
        <div className="grid gap-4 md:grid-cols-2">
          <Field label={t('cauHinhOcr.fTen')} required error={loiTen}>
            {(p) => (
              <Input {...p} disabled={!coSua} maxLength={255} value={ban.name} onChange={(e) => doi('name', e.target.value)} />
            )}
          </Field>
          <Field label={t('cauHinhOcr.fLoai')} required error={loiServer?.truong['/type']}>
            {(p) => (
              <Select<DocumentType>
                {...p}
                disabled={!coSua}
                className="w-full"
                value={ban.type}
                onChange={(v) => doi('type', v)}
                options={DOCUMENT_TYPES.map((v) => ({ value: v, label: t(NHAN_LOAI[v]) }))}
              />
            )}
          </Field>
        </div>
        <Field label={t('cauHinhOcr.fMoTa')} hint={t('cauHinhOcr.fMoTaGoiY')} error={loiServer?.truong['/description']}>
          {(p) => (
            <Textarea
              {...p}
              disabled={!coSua}
              rows={4}
              value={ban.description}
              onChange={(e) => doi('description', e.target.value)}
            />
          )}
        </Field>
        <div className="grid gap-4 md:grid-cols-3">
          <Field label={t('cauHinhOcr.fCheDo')} hint={t(MO_CHE_DO[ban.extraction_mode])} error={loiServer?.truong['/extraction_mode']}>
            {(p) => (
              <Select<ExtractionMode>
                {...p}
                disabled={!coSua}
                className="w-full"
                value={ban.extraction_mode}
                onChange={(v) => doi('extraction_mode', v)}
                options={EXTRACTION_MODES.map((v) => ({ value: v, label: t(NHAN_CHE_DO[v]) }))}
              />
            )}
          </Field>
          {ban.extraction_mode === 'OCR_THEN_MAP' && (
            <Field label={t('cauHinhOcr.fModelDoc')} required error={loiDoc}>
              {(p) => (
                <ChonModel {...p} disabled={!coSua} vai="doc" value={ban.ocr_reader_provider} onChange={(v) => doi('ocr_reader_provider', v)} />
              )}
            </Field>
          )}
          <Field
            label={t(ban.extraction_mode === 'OCR_THEN_MAP' ? 'cauHinhOcr.fModelDien' : 'cauHinhOcr.fModelMot')}
            error={loiServer?.truong['/ocr_provider']}
          >
            {(p) => <ChonModel {...p} disabled={!coSua} vai="dien" value={ban.ocr_provider} onChange={(v) => doi('ocr_provider', v)} />}
          </Field>
        </div>
        <fieldset className="grid gap-3 md:grid-cols-2">
          <legend className="mb-2 text-xs font-medium text-content-secondary">{t('cauHinhOcr.coXuLy')}</legend>
          {CO_XU_LY.map((k) => (
            <div key={k} className="min-w-0">
              <Checkbox disabled={!coSua} checked={ban[k]} onChange={(e) => doi(k, e.target.checked)}>
                <span className="text-sm text-content-primary">{t(NHAN_CO[k].nhan)}</span>
              </Checkbox>
              <p className="ml-6 mt-0.5 text-xs text-content-muted">{t(NHAN_CO[k].mo)}</p>
            </div>
          ))}
          <div className="min-w-0">
            <Checkbox disabled={!coSua} checked={ban.is_active} onChange={(e) => doi('is_active', e.target.checked)}>
              <span className="text-sm text-content-primary">{t('cauHinhOcr.fDangDung')}</span>
            </Checkbox>
            <p className="ml-6 mt-0.5 text-xs text-content-muted">{t('cauHinhOcr.fDangDungMo')}</p>
          </div>
        </fieldset>
      </TileBody>
    </Tile>
  );
}
